import { inject, Injectable } from "@angular/core";
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpResponse, HttpErrorResponse, HttpContextToken } from "@angular/common/http";
import { Observable, tap } from "rxjs";
import { AuthService } from "../../auth/services/auth.service";
import { Router } from "@angular/router";
import { ToastService } from "@app/kernel/tools/message/message.config";
import { DISABLE_TOAST } from "../../auth/tokens/disable-toast.token";
import { DISABLE_AUTH_HANDLE } from "../../auth/tokens/disable-auth-handle.token";
import { isObject } from "@app/kernel/shared/helpers";
import { InterpolationParameters } from "@ngx-translate/core";
import { MaintenanceBreakService } from "@app/shared/settings/system-settings/services/maintenance-break.service";

/**
 * An HTTP interceptor that globally handles error responses for REST and GraphQL requests.
 * It supports automatic toast notifications for errors and can redirect to the login page on 401 errors.
 * The error's `data` fills the `{{...}}` placeholders of the translated message.
 *
 * You can disable toast notifications or authorization handling per request using the `DISABLE_TOAST` and `DISABLE_AUTH_HANDLE` context tokens.
 *
 * Example of disabling toast notifications on a specific request:
 * ```ts
 * import { HttpContext } from '@angular/common/http';
 * import { DISABLE_TOAST } from './tokens/disable-toast.token';
 *
 * this.http.get('/api/silent', {
 *   context: new HttpContext().set(DISABLE_TOAST, true)
 * });
 * ```
 */
@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  private authService = inject(AuthService);
  private maintenanceBreakService = inject(MaintenanceBreakService);
  private router = inject(Router);
  private toast = inject(ToastService);

  /**
   * Intercepts all outgoing HTTP requests and processes error responses.
   *
   * @param req - The outgoing HTTP request.
   * @param next - The HTTP request handler.
   * @returns An observable stream of HTTP events.
   */
  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const disableToastFn = this.getDisableToastFn(req);
    const disableAuthHandleFn = this.getDisableAuthHandleFn(req);

    return next.handle(req).pipe(
      tap({
        next: event => {
          // Handles GraphQL-style responses (arrays with errors)
          if (event instanceof HttpResponse && Array.isArray(event.body)) {
            const isUnauthorized = this.handleGraphQLErrors(event.body, disableToastFn, disableAuthHandleFn);
            if (isUnauthorized) {
              this.authService.clearAuthData();
              this.router.navigate(["/auth/login"]);
            }
          }
        },
        error: (response: HttpErrorResponse) => {
          // Handle REST API errors
          if (response.status === 401 && !disableAuthHandleFn(response.error)) {
            this.authService.clearAuthData();
            this.router.navigate(["/auth/login"]);
          }

          // Handle error toast notifications
          if (!disableToastFn(response.error)) {
            const messageKey = response.error?.data?.messageKey ?? response.error?.message ?? "ERRORS.SOMETHING_WENT_WRONG";
            this.toast.error(messageKey, "ERRORS.SUMMARY", { detailTranslationValue: this.getTranslationValues(response.error?.data) });
          }
        },
      })
    );
  }

  /**
   * Handles errors in GraphQL-style response arrays.
   *
   * @param responseItems - The array of GraphQL response objects containing error information.
   * @param disableToastFn - A function that determines whether toast notifications should be suppressed.
   * @param disableAuthHandleFn - A function that determines whether authorization handling should be suppressed.
   * @returns `true` if an unauthorized error (401) was found, otherwise `false`.
   */
  private handleGraphQLErrors(
    responseItems: unknown[],
    disableToastFn: (error: unknown) => boolean | null,
    disableAuthHandleFn: (error: unknown) => boolean | null
  ): boolean {
    let isUnauthorized = false;

    for (const item of responseItems) {
      const gqlErrors = isObject(item) && "errors" in item && item?.errors;
      if (!Array.isArray(gqlErrors)) continue;

      for (const error of gqlErrors) {
        const formatted = error?.extensions?.formatted ?? error?.extensions;
        const { code, type, message, data = error?.extensions } = formatted || {};

        if (data?.reason === "MaintenanceBreak") {
          this.maintenanceBreakService.handleMaintenanceBreakResponse(data);
        } else {
          // Check if the error is an unauthorized error (401)
          isUnauthorized ||= (code === 401 || type === "UNAUTHORIZED") && !disableAuthHandleFn(formatted);

          // Display error toast if necessary
          if (!disableToastFn(formatted)) {
            const fieldMessageKeys = this.getFieldMessageKeys(data);
            if (fieldMessageKeys.length) {
              fieldMessageKeys.forEach(messageKey => this.toast.error(messageKey, "ERRORS.SUMMARY"));
            } else {
              const messageKey = data?.messageKey ?? message ?? "ERRORS.SOMETHING_WENT_WRONG";
              this.toast.error(messageKey, "ERRORS.SUMMARY", { detailTranslationValue: this.getTranslationValues(data) });
            }
          }
        }
      }
    }

    return isUnauthorized;
  }

  /**
   * Returns the distinct message keys of a validation error's field errors, which
   * a backend rule such as `existsWhere` sets, so each is toasted once.
   * @param data - The `data` of the error response: the field errors for a validation error.
   * @returns The distinct keys, or an empty array when `data` is not a list of field errors.
   */
  private getFieldMessageKeys(data: unknown): string[] {
    if (!Array.isArray(data)) return [];
    const keys = data.map((fieldError: unknown) => (isObject(fieldError) && "messageKey" in fieldError ? fieldError.messageKey : undefined));
    return [...new Set(keys.filter((key): key is string => typeof key === "string" && key !== ""))];
  }

  /**
   * Returns the error data as interpolation values, so a message such as
   * `"{{plateNumber}} is out on {{movementCode}}"` is filled from the backend error.
   * @param data - The `data` object of the error response.
   * @returns The data when it is an object, otherwise `undefined`.
   */
  private getTranslationValues(data: unknown): InterpolationParameters | undefined {
    return isObject(data) ? (data as InterpolationParameters) : undefined;
  }

  /**
   * Returns a function to check whether toast notifications should be suppressed for the request.
   * @param req - The HTTP request object.
   * @returns A function to determine if toast should be disabled for the request.
   */
  private getDisableToastFn(req: HttpRequest<unknown>) {
    return this.getDisableFn(req, DISABLE_TOAST, "X-Disable-Toast", "X-Disable-Toast-If-Reason");
  }

  /**
   * Returns a function to check whether authorization handling should be suppressed for the request.
   * @param req - The HTTP request object.
   * @returns A function to determine if authorization handling should be disabled.
   */
  private getDisableAuthHandleFn(req: HttpRequest<unknown>) {
    return this.getDisableFn(req, DISABLE_AUTH_HANDLE, "X-Disable-Auth-Handle", "X-Disable-Auth-Handle-If-Reason");
  }

  /**
   * Returns a function that checks whether toast notifications or authorization handling should be suppressed for the given HTTP request.
   * The function considers multiple mechanisms for disabling the behavior:
   *
   * - A custom function provided via the `contextToken` that can return `true` or `false`.
   * - A boolean `true` value directly set via the `contextToken` to always disable the behavior.
   * - A request header `X-Disable-Toast: true` (or `X-Disable-Auth-Handle: true`) to globally disable the behavior.
   * - A request header `X-Disable-Toast-If-Reason: some_reason` (or `X-Disable-Auth-Handle-If-Reason: some_reason`) to disable the behavior only if the response error matches the specified reason.
   *
   * @param req - The HTTP request object.
   * @param contextToken - The context token used to retrieve the custom function or value from the request context.
   * @param headerName - The name of the header that globally disables the behavior (e.g., `X-Disable-Toast`).
   * @param headerByReasonName - The name of the header that disables the behavior only if a specific error reason is present (e.g., `X-Disable-Toast-If-Reason`).
   * @returns A function that checks if the specified behavior (toast notification or authorization handling) should be disabled for the request.
   */
  private getDisableFn(
    req: HttpRequest<unknown>,
    contextToken: HttpContextToken<boolean | ((error: unknown) => boolean | null) | null>,
    headerName: string,
    headerByReasonName: string
  ) {
    const disable = req.context.get(contextToken);
    const disableHeader = req.headers.get(headerName) === "true";
    const disableByReasonHeader = req.headers.get(headerByReasonName);
    return typeof disable === "function"
      ? disable
      : (error: unknown) =>
          disable ??
          (disableByReasonHeader === ((error as Record<string, unknown>)?.["data"] as Record<string, unknown>)?.["reason"] || disableHeader);
  }
}
